/**
 * Admin › Tokens — DESIGN.md §12.5.
 *
 * "owner-revocable, admins see/revoke all (audited)."
 *
 * The oversight view. It exists because a token acts as a person: when someone
 * leaves, or a laptop is lost, an administrator has to be able to see every
 * key that can act as anyone and cut it off — without waiting for its owner.
 */
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';
import type { ApiToken } from '@slate/shared';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingRegion } from '@/components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TokenList } from '@/components/tokens/token-list';
import { useAllTokens, useTokenMutations, isLive } from '@/features/tokens/api';

export const Route = createFileRoute('/admin/tokens')({
  component: AdminTokensPage,
});

function AdminTokensPage(): JSX.Element {
  const tokens = useAllTokens();
  const mutations = useTokenMutations('admin');
  const [confirm, setConfirm] = useState<ApiToken | null>(null);

  if (tokens.isLoading) return <LoadingRegion label="Loading tokens" />;

  const all = tokens.data ?? [];
  const live = all.filter(isLive);

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground max-w-2xl text-sm">
        Every personal access token in the workspace. A token acts as its owner with their
        permissions — never as an administrator, whatever their role — and everything it does is
        audited as an agent action against them. Revoking one here is immediate and audited as an
        administrator action.
      </p>

      {all.length === 0 ? (
        <EmptyState
          icon={<KeyRound className="size-8" />}
          title="No tokens have been created"
          description="Members create their own in Settings › API & Agents."
        />
      ) : (
        <>
          <p className="text-muted-foreground text-xs">
            {live.length} live, {all.length - live.length} revoked or expired.
          </p>
          <div className="bg-card rounded-lg border">
            <TokenList
              tokens={all}
              showOwner
              onRevoke={setConfirm}
              revoking={mutations.revoke.isPending}
            />
          </div>
        </>
      )}

      <Dialog
        open={confirm !== null}
        onOpenChange={(open) => (open ? undefined : setConfirm(null))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Revoke “{confirm?.name}” — {confirm?.owner.name}’s token?
            </DialogTitle>
            <DialogDescription>
              Anything using it stops working immediately, and the revocation is recorded against
              you. Everything the token already did stays in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={mutations.revoke.isPending}
              onClick={() =>
                confirm &&
                mutations.revoke.mutate(confirm.id, { onSuccess: () => setConfirm(null) })
              }
            >
              Revoke token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
